/**
 * Firmio Voice — client for /api/voice-assistant (the GPT-Live voice assistant).
 *
 * Only the WebRTC handshake and the close go through our backend; the audio
 * and the live events flow between the browser and OpenAI directly (see
 * hooks/useVoiceAssistant.ts).
 */
import { apiBase } from './apiBase';
import { apiFetch, ApiError } from './api';
import { useAuthStore } from '../store/useAuthStore';

interface Envelope<T> {
  success: true;
  data: T;
}

export interface VoiceAssistantStatus {
  /** False when no OpenAI key is configured or the feature is switched off. */
  enabled: boolean;
  model: string;
  backendModel: string;
  voice: string;
  maxMinutes: number;
}

export interface VoiceSessionCreated {
  sessionId: string;
  /** The SDP answer to apply with setRemoteDescription. */
  sdp: string;
  /** The Firmio AI conversation the transcript is saved under. */
  conversationId: string;
  maxMinutes: number;
  model: string;
  backendModel: string;
  voice: string;
}

export interface VoiceSessionClosed {
  conversationId: string;
  seconds: number;
  /** Drafts (tasks / reminders / meetings) waiting for approval in the chat. */
  pendingSuggestions: number;
}

export interface VoiceSessionSnapshot {
  sessionId: string;
  conversationId: string;
  status: 'connecting' | 'live' | 'closing' | 'closed';
  turns: { role: 'user' | 'assistant'; content: string }[];
  seconds: number;
  activity: 'idle' | 'thinking' | 'tool';
  lastError: string | null;
  closeReason: string | null;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function getVoiceAssistantStatus(): Promise<VoiceAssistantStatus> {
  const res = await apiFetch<Envelope<VoiceAssistantStatus>>('/api/voice-assistant/status', {
    headers: authHeaders(),
  });
  return res.data;
}

/** Exchange the browser's SDP offer for OpenAI's answer through our backend. */
export async function createVoiceSession(sdp: string): Promise<VoiceSessionCreated> {
  const res = await apiFetch<Envelope<VoiceSessionCreated>>('/api/voice-assistant/sessions', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ sdp }),
  });
  return res.data;
}

export async function closeVoiceSession(
  sessionId: string,
  reason?: string,
): Promise<VoiceSessionClosed> {
  const res = await apiFetch<Envelope<VoiceSessionClosed>>(
    `/api/voice-assistant/sessions/${encodeURIComponent(sessionId)}/close`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  return res.data;
}

export async function getVoiceSession(sessionId: string): Promise<VoiceSessionSnapshot> {
  const res = await apiFetch<Envelope<VoiceSessionSnapshot>>(
    `/api/voice-assistant/sessions/${encodeURIComponent(sessionId)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export interface LiveVoice {
  id: string;
  label: string;
  description: string;
  group: 'recommended' | 'live' | 'classic';
}

export interface VoiceCatalog {
  voices: LiveVoice[];
  /** The voice this user hears. */
  selected: string;
  /** The install default (what a user with no pick hears). */
  default: string;
  /** The greeting every sample reads, personalised for this user. */
  greeting: string;
}

export async function listVoices(): Promise<VoiceCatalog> {
  const res = await apiFetch<Envelope<VoiceCatalog>>('/api/voice-assistant/voices', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function selectVoice(voice: string): Promise<string> {
  const res = await apiFetch<Envelope<{ selected: string }>>(
    '/api/voice-assistant/voices/selected',
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ voice }) },
  );
  return res.data.selected;
}

/**
 * The WAV greeting in `voice`, as raw bytes for `decodeAudioData`. The first
 * request for a voice makes the server generate it (a few seconds); later
 * ones are cached.
 */
export async function fetchVoiceSample(voice: string): Promise<ArrayBuffer> {
  const res = await fetch(
    `${apiBase()}/api/voice-assistant/voices/${encodeURIComponent(voice)}/sample`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    let message = `Mostra nu a putut fi generată (HTTP ${res.status}).`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { error?: { message?: string; code?: string } };
      message = body.error?.message ?? message;
      code = body.error?.code;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, message, code);
  }
  return res.arrayBuffer();
}

/** The sample's URL, for players that stream it themselves (the mobile app). */
export function voiceSampleUrl(voice: string): string {
  return `${apiBase()}/api/voice-assistant/voices/${encodeURIComponent(voice)}/sample`;
}

// --- Drafts proposed during a conversation ----------------------------------

export type VoiceSuggestionKind = 'TASK' | 'REMINDER' | 'MEETING' | 'ACTION';
export type VoiceSuggestionStatus = 'PENDING' | 'ACCEPTED' | 'DISMISSED';

export interface VoiceSuggestion {
  id: string;
  conversationId: string;
  kind: VoiceSuggestionKind;
  /** The chat tool replayed on accept (task_update, lead_create, …). */
  toolName: string | null;
  title: string;
  /** The tool arguments as the assistant proposed them (projectName, dueDate, invitees, …). */
  payload: Record<string, unknown>;
  status: VoiceSuggestionStatus;
  resultId: string | null;
  resultLabel: string | null;
  error: string | null;
  decidedAt: string | null;
  position: number;
  createdAt: string;
}

export async function listVoiceSuggestions(conversationId: string): Promise<VoiceSuggestion[]> {
  const res = await apiFetch<Envelope<{ suggestions: VoiceSuggestion[] }>>(
    `/api/voice-assistant/conversations/${encodeURIComponent(conversationId)}/suggestions`,
    { headers: authHeaders() },
  );
  return res.data.suggestions;
}

async function decide(
  id: string,
  action: 'accept' | 'dismiss' | 'restore',
): Promise<VoiceSuggestion> {
  const res = await apiFetch<Envelope<VoiceSuggestion>>(
    `/api/voice-assistant/suggestions/${encodeURIComponent(id)}/${action}`,
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}

/** Runs the real action (creates the task / reminder / Meet meeting + invites). */
export function acceptVoiceSuggestion(id: string): Promise<VoiceSuggestion> {
  return decide(id, 'accept');
}
export function dismissVoiceSuggestion(id: string): Promise<VoiceSuggestion> {
  return decide(id, 'dismiss');
}
export function restoreVoiceSuggestion(id: string): Promise<VoiceSuggestion> {
  return decide(id, 'restore');
}
